import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of, forkJoin } from 'rxjs';
import {
  map,
  switchMap,
  catchError
} from 'rxjs/operators';
import {
  loadSettings,
  loadSettingsSuccess,
  loadSettingsFail,
  loadCountryList,
  loadCountryListSuccess,
  loadCountryListFail,
  loadStackTypeDetail,
  loadStackTypeDetailSuccess,
  loadStackTypeDetailFail,
  measureLocationPingsSuccess
} from './settings-base.action';
import { SettingsBaseApi } from './settings-base.api';
import { LocationPingService } from './location-ping.service';

@Injectable()
export class SettingsBaseEffect {

  private _onLoadSettings$ = createEffect(() => this._actions$.pipe(
    ofType(loadSettings),
    switchMap((action) => this._api
      .settings$(action.data)
      .pipe(
        map((response) => loadSettingsSuccess(response, action)),
        catchError((err) => of(loadSettingsFail(err, action)))
      )
    )
  ));

  private _onLoadStackTypeDetail$ = createEffect(() => this._actions$.pipe(
    ofType(loadStackTypeDetail),
    switchMap((action) => this._api
      .stackTypeDetail$(action.data.clientId, action.data.stackTypeId)
      .pipe(
        map((response) => loadStackTypeDetailSuccess(response, action)),
        catchError((err) => of(loadStackTypeDetailFail(err, action)))
      )
    )
  ));

  private _onLoadCountryList$ = createEffect(() => this._actions$.pipe(
    ofType(loadCountryList),
    switchMap((action) => this._api
      .countryList$()
      .pipe(
        map((response) => loadCountryListSuccess(response, action)),
        catchError((err) => of(loadCountryListFail(err, action)))
      )
    )
  ));

  private _onLoadSettingsSuccessMeasurePings$ = createEffect(() => this._actions$.pipe(
    ofType(loadSettingsSuccess),
    switchMap(({ data }) => {
      const locations = data.locationList || [];

      if (!locations || locations.length === 0) {
        return of(measureLocationPingsSuccess({
          locationPingMap: {},
          fastestLocationId: null
        }, null));
      }

      if (locations.length === 1) {
        return of(measureLocationPingsSuccess({
          locationPingMap: { [locations[0].id]: null },
          fastestLocationId: locations[0].id
        }, null));
      }

      // Measure all pings in parallel
      const pingObservables = locations.map((location) =>
        this._locationPingService.measurePing(location).pipe(
          map((ping) => ({ locationId: location.id, ping })),
          catchError(() => of({ locationId: location.id, ping: null }))
        )
      );

      return forkJoin(pingObservables).pipe(
        map((results) => {
          // Build ping map
          const locationPingMap: Record<string, number | null> = {};
          results.forEach((result) => {
            locationPingMap[result.locationId] = result.ping;
          });

          // Find fastest location
          const successfulPings = results.filter(
            (r): r is typeof r & { ping: number } => r.ping !== null
          );
          let fastestLocationId: string | null = null;

          if (successfulPings.length > 0) {
            const sorted = successfulPings.sort((a, b) => a.ping - b.ping);
            fastestLocationId = sorted[0].locationId;
          } else {
            // Fallback to first location if all pings failed
            fastestLocationId = locations[0].id;
          }

          return measureLocationPingsSuccess({
            locationPingMap,
            fastestLocationId
          }, null);
        })
      );
    })
  ));

  constructor(
    private _actions$: Actions<any>,
    private _api: SettingsBaseApi,
    private _locationPingService: LocationPingService
  ) { }
}
